import { API_BASE, parseResponse } from './api';

export function uploadExcelJob(file, onProgress) {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/excel-jobs/upload`, true);
    
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    };
    
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (e) {
          resolve(xhr.responseText);
        }
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.error || 'Upload failed'));
        } catch (e) {
          reject(new Error('Upload failed'));
        }
      }
    };
    
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(formData);
  });
}

export async function getJobs() {
  const response = await fetch(`${API_BASE}/excel-jobs`);
  return parseResponse(response);
}

export async function getJob(id) {
  const response = await fetch(`${API_BASE}/excel-jobs/${encodeURIComponent(id)}`);
  return parseResponse(response);
}

export async function deleteJob(id) {
  const response = await fetch(`${API_BASE}/excel-jobs/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
  return parseResponse(response);
}

export async function getJobRecords(id, params = {}) {
  const query = new URLSearchParams(params).toString();
  const response = await fetch(`${API_BASE}/excel-jobs/${encodeURIComponent(id)}/records?${query}`);
  return parseResponse(response);
}

export async function removeDuplicates(jobId, columns) {
  const response = await fetch(`${API_BASE}/excel-jobs/${jobId}/remove-duplicates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ columns })
  });
  return await parseResponse(response);
}

export async function calculateDuplicates(jobId, columns) {
  const response = await fetch(`${API_BASE}/excel-jobs/${jobId}/calculate-duplicates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ columns })
  });
  return await parseResponse(response);
}

export async function deleteByPrefix(id, column, prefixes) {
  const response = await fetch(`${API_BASE}/excel-jobs/${encodeURIComponent(id)}/delete-by-prefix`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ column, prefixes })
  });
  return parseResponse(response);
}

export async function compareExcelJobs(jobId1, jobId2, columns) {
  const response = await fetch(`${API_BASE}/excel-jobs/compare`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jobId1, jobId2, columns })
  });
  return parseResponse(response);
}

export async function exportExcelJob(id) {
  const response = await fetch(`${API_BASE}/excel-jobs/${encodeURIComponent(id)}/export`, {
    method: 'POST'
  });
  return parseResponse(response);
}

export function getExcelDownloadUrl(filename) {
  return `${API_BASE}/../downloads/${filename}`;
}

// ==============================
// MULTI-FILE OPERATIONS
// ==============================

export async function getOperations() {
  const response = await fetch(`${API_BASE}/operations`);
  return parseResponse(response);
}

export async function getOperation(id) {
  const response = await fetch(`${API_BASE}/operations/${encodeURIComponent(id)}`);
  return parseResponse(response);
}

export async function deleteOperation(id) {
  const response = await fetch(`${API_BASE}/operations/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
  return parseResponse(response);
}

export async function deduplicateOperation(targetJobs, columns, priority = 'first') {
  const response = await fetch(`${API_BASE}/operations/deduplicate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetJobs, columns, priority })
  });
  return await parseResponse(response);
}

export async function deletePrefixOperation(targetJobs, column, prefixes) {
  const response = await fetch(`${API_BASE}/operations/delete-prefix`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetJobs, column, prefixes })
  });
  return await parseResponse(response);
}

export async function compareOperation(baseJobs, referenceJobs, columns, normalization) {
  const response = await fetch(`${API_BASE}/operations/compare`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ baseJobs, referenceJobs, columns, normalization })
  });
  return await parseResponse(response);
}

export async function combineOperation(targetJobs, removeDuplicates, columns) {
  const response = await fetch(`${API_BASE}/operations/combine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetJobs, removeDuplicates, columns })
  });
  return await parseResponse(response);
}
